// Utils/tamil.js
//
// BiLabel (added — stacked bilingual display) renders the Tamil text on
// its own line below the English instead of inline in parentheses, for
// table headers / field labels / static copy. Button text keeps the
// inline bi() form (a stacked two-line button reads badly and buttons
// are short-lived action words, not reference copy) — call sites decide
// which to use, this module doesn't guess from context.
import { Box } from "@mui/material";
//
// Central Tamil-translation dictionary, added 2026-07-29 per client
// request ("adding tamil words in () for lables but make sure this not
// breaks any ui"). ONE dictionary, not scattered inline strings per
// page — so the same English label always gets the same Tamil text
// everywhere, and a translation only ever needs fixing in one place.
//
// bi(label) -> "English (Tamil)" when a translation exists, otherwise
// just the English label unchanged (never throws, never renders a
// literal "undefined" — safe to call on any string, translated or not).
// Deliberately NOT applied to dynamic/DB-sourced text (product names,
// company names, user-entered remarks, etc.) — only to static UI copy
// (headings, button text, form field labels, nav items, table headers)
// where a fixed translation makes sense.
const TAMIL_LABELS = {
  // ---- Common actions ----
  "Submit": "சமர்ப்பிக்கவும்",
  "Save": "சேமிக்கவும்",
  "Cancel": "ரத்துசெய்",
  "Update": "புதுப்பிக்கவும்",
  "Edit": "திருத்து",
  "Delete": "நீக்கு",
  "Add": "சேர்",
  "Contact Details": "தொடர்பு விவரங்கள்",
  "Name Of The Participant": "பங்கேற்பாளரின் பெயர்",
  "Registered Participants": "பதிவுசெய்யப்பட்ட பங்கேற்பாளர்கள்",
  // "Clear": "அழி",
  // "Filter": "வடிகட்டு",
  // "Filters": "வடிகட்டிகள்",
  "Indented Qty": "தேவைப்பட்ட அளவு",
  // "Back": "பின்செல்",
  "Close": "மூடுக",
  "Search": "தேடு",
  "Login": "உள்நுழைவு",
  "Logout": "வெளியேறு",
  "Approve": "ஒப்புதல்",
  "Reject": "நிராகரி",
  "Reject Bid": "விலைப்புள்ளியை நிராகரி",
  // "Undo": "மீளமை",
  "Print": "அச்சிடு",
  "Export": "பதிவிறக்கு",
  "View": "பார்வையிடு",
  "Confirm": "உறுதிப்படுத்து",
  "Details": "விவரங்கள்",
  // "Remarks": "குறிப்புகள்",
  "Status": "நிலை",
  "Actions": "செயல்கள்",

  // ---- Nav / sections ----
  // "Dashboard": "டாஷ்போர்டு",
  "Products": "பொருட்கள்",
  "Procurement": "கொள்முதல்",
  "Pending Procurement": "நிலுவையிலுள்ள கொள்முதல்",
  "Deliveries": "விநியோகங்கள்",
  "Certificates": "சான்றிதழ்கள்",
  "Supplier Queue": "விநியகோகஸ்தர்கள் வரிசை",
  "Indent Review": "தேவைபட்டியல் மறுஆய்வு",
  // "Supply Monitoring": "பொருட்கள் விநியோக கண்காணிப்பு",
  "CWS Stores": "கூட்டுறவு பண்டகசாலைகள் ",
  "Price Review": "விலை மறுஆய்வு",
  "Quality Check": "தரக் கட்டுப்பாடு",
  "Rate Fixing": "விலை நிர்ணயம்",
  // Client 2026-08-05: the committee's own page is titled
  // "Product Rate Fixation" — same Tamil term for the rate-fixing part.
  "Rate Fixation": "விலை நிர்ணயம்",
  "Product Rate Fixation": "பொருள் விலை நிர்ணயம்",
  "Product Lists": "பொருட்கள் பட்டியல்",
  "My Indents": "எனது தேவைப்பட்டியல்",
  "Appearance": "தோற்றம்",
  "Supplier Portal": "விநியோகஸ்தர்கள் தளம்",
  "Procurement Portal": "கொள்முதல் தளம்",

  // ---- Registration / profile ----
  "Mobile Number": "கைபேசி எண்",
  "Email": "மின்னஞ்சல்",
  "Password": "கடவுச்சொல்",
  "OTP": "ஒரு முறை கடவுச்சொல்",
  "Full Name": "முழு பெயர்",
  "Supplier Name": "விநியோகஸ்தர் பெயர்",
  // "GSTIN": "ஜிஎஸ்டிஐஎன்",
  // "PAN Number": "பான் எண்",
  "Bank Details": "வங்கி விவரங்கள்",
  "Account Number": "கணக்கு எண்",
  "IFSC Code": "ஐஎஃப்எஸ்சி குறியீடு",
  "Address": "முகவரி",
  "District": "மாவட்டம்",
  "Documents": "ஆவணங்கள்",
  "Basic Info": "அடிப்படை தகவல்",
  "Register": "பதிவு செய்யவும்",
  "Resubmit": "மீண்டும் சமர்ப்பிக்கவும்",

  // ---- Indent / tender / product domain ----
  "Indent": " தேவைப்பட்டியல்",
  "Indent No.": " தேவைப்பட்டியல் எண்",
  "Month": "மாதம்",
  // "For Month": "மாதத்திற்கு",
  "Quantity": "அளவு",
  // "Product": "பொருள்",
  "Product": "பொருட்கள்",
  "Company": "நிறுவனம்",
  "Product Photos": "பொருட்களின் புகைப்படம்",
  "Rate": "விலை",
  "Create New Indent": "தேவைப்பட்டியலை உருவாக்கு",
  "Create Indent": "தேவைப்பட்டியலை உருவாக்கு ",
  "Supplier": "பொருள் வழங்குநர்",
  "Suppliers": "பொருள் வழங்குநர்கள்",
  "Offered Quantity": "வழங்கப்பட்ட அளவு",
  "Delivery": "வழங்கப்படும் அளவு",
  "Confirm Receipt": "பொருட்கள் பெறப்பட்டதற்கான ஒப்புதல்",
  "Received Date": "பெறப்பட்ட தேதி",
  "Photo": "புகைப்படம்",
  // "Consignment": "சரக்கு",
  "Market Price": "சந்தை விலை",
  // Client 2026-08-05: on the Rate Fixation page this value is called
  // "Committee Fixed Price" — it's the committee's own fixed rate, not a
  // raw market observation. Price Review deliberately still says "Market
  // Price" for the same column (client's explicit choice).
  "Committee Fixed Price": "குழு நிர்ணயித்த விலை",
  "Eye Sight Report": "பார்வை அறிக்கை",
  "Negotiation": "பேச்சுவார்த்தை",
  // "Negotiate": "விலை நிர்ணயம்",
  "Supply Order": "கொள்முதல் ஆணை",
  // Added 2026-08-08 for the new supplier-wide/store-wide consolidated
  // Supply Order buttons (TenderDetail.jsx, IndentDetail.jsx,
  // IndentReviewDetail.jsx, SupplierTenders.jsx).
  // "Supplier Supply Orders": "விநியோகஸ்தர் கொள்முதல் ஆணைகள்",
  // "Download Supply Order": "கொள்முதல் ஆணையைப் பதிவிறக்கவும்",
  // "Download My Supply Order": "எனது கொள்முதல் ஆணையைப் பதிவிறக்கவும்",

  // ---- Product Lists / Product List Assignments / Product Master ----
  "New List": "புதிய பட்டியல்",
  "Rename List": "பட்டியலை மறுபெயரிடு",
  "List Name": "பட்டியல் பெயர்",
  "Description": "விவரம்",
  "Consignment list": "சரக்கு விவரங்கள்/பட்டியல்",
  "Assign Products": "பொருட்கள் ஒதுக்கீடு",
  "Delete List": "பட்டியலை நீக்கு",
  "Total Lists": "மொத்த பட்டியல்கள்",
  "Active Lists": "செயலில் உள்ள பட்டியல்கள்",
  "Products Assigned": "ஒதுக்கப்பட்ட பொருட்கள்",
  // "Active": "செயலில்",
  // "Inactive": "செயலற்ற",
  "No lists yet.": "இதுவரை பட்டியல்கள் இல்லை.",
  "Current List": "தற்போதைய பட்டியல்",
  "Unassigned": "ஒதுக்கப்படாதது",
  "Select all visible": "தெரியும் அனைத்தையும் தேர்ந்தெடு",
  "Clear selection": "தேர்வை அழி",
  "All Products": "அனைத்து பொருட்கள்",
  "Search products": "பொருட்களைத் தேடு",
  "No matching products.": "பொருந்தும் பொருட்கள் இல்லை.",
  "Back to Product Lists": "பொருட்கள் பட்டியலுக்குத் திரும்பு",
  "Product List Assignments": "பொருட்கள் பட்டியல் ஒதுக்கீடுகள்",
  "Product List": "பொருட்கள் பட்டியல்",
  "Add Product": "பொருளைச் சேர்",
  "Edit Product": "பொருளைத் மாற்றியமைத்தல்",
  "Total products": "மொத்த பொருட்கள்",
  // "Short Name": "குறுகிய பெயர்",
  "Remove Product": "பொருளை அகற்று",
  "Remove": "அகற்று",
  "No products yet — add one to get started.": "இதுவரை பொருட்கள் இல்லை — தொடங்க ஒன்றைச் சேர்க்கவும்.",
  "No products match this filter.": "இந்த வடிகட்டலுக்கு பொருட்கள் பொருந்தவில்லை.",
  "Remove this product from the list?": "இந்த பொருளை பட்டியலில் இருந்து அகற்றவா?",
  "It will no longer be selectable in new indents, but existing indents that already reference it are unaffected.": "இது புதிய தேவைப்பட்டியல் தேர்வு செய்ய முடியாது, ஆனால் ஏற்கனவே இதைக் குறிப்பிடும் தேவைப்பட்டியல் பாதிக்கப்படாது.",

  // ---- Supply monitoring / CWS stores / procurement products ----
  // "Store": "பண்டக சாலை",
  // "Month": "மாதம்",
  "Received Date / Proof": "பெறப்பட்ட தேதி / சான்று",
  "No matching supply orders.": "பொருந்தும் கொள்முதல் ஆர்டர்கள் இல்லை.",
  "Clear month": "மாதத்தை அழி",
  // "View Photo": "புகைப்படத்தைப் பார்",
  // "View Proof": "சான்றைப் பார்",
  "Product Code": "பொருள் குறியீடு",
  "Product Name": "பொருள் பெயர்",
  // "Product Name (English)": "பொருள் பெயர் (ஆங்கிலம்)",
  // "Product Name (Tamil)": "பொருள் பெயர் (தமிழ்)",
  "Search by product name (English or Tamil)": "பொருள் பெயரால் தேடு (ஆங்கிலம் அல்லது தமிழ்)",
  // "No products are available yet.": "இன்னும் பொருட்கள் எதுவும் இல்லை.",
  // "No products match your search.": "உங்கள் தேடலுக்கு பொருந்தும் பொருட்கள் இல்லை.",
  "Store Name": "பண்டகசாலையின் பெயர்",
  // "Display Name": "காட்சி பெயர்",
  "District / City": "மாவட்டம் / நகரம்",
  "Contact": "தொடர்பு",
  "Search company name, code, or city": "நிறுவனத்தின் பெயர், குறியீடு, அல்லது நகரத்தால் தேடு",
  "Consumer Wholesale Stores registered in the system.": " பதிவு செய்யப்பட்ட மொத்த விற்பனை பண்டகசாலைகள்.",
  "No CWS stores match this filter.": "இந்த வடிகட்டலுக்கு பொருந்தும் சிடபிள்யூஎஸ் கடைகள் இல்லை.",
  "Every awarded supply-order line across every store — whether the product has actually been supplied yet.": "ஒவ்வொரு கடையிலும் உள்ள ஒவ்வொரு கொள்முதல் ஆர்டர் வரியும் — பொருள் உண்மையில் வழங்கப்பட்டதா என்பது.",
  "Products currently available for procurement — shown in both English and Tamil.": "தற்போது கொள்முதலுக்குக் கிடைக்கும் பொருட்கள் — ஆங்கிலம் மற்றும் தமிழ் இரண்டிலும் காட்டப்படுகிறது.",

  // ---- Supplier Queue / Supplier Detail (admin review) ----
  "Mobile": "தொலைப்பேசி",
  "Authorised Signatory": "அங்கீகரிக்கப்பட்ட கையொப்பமிடுபவர்",
  "Year of Establishment": "நிறுவப்பட்ட ஆண்டு",
  "Base District": "அடிப்படை மாவட்டம்",
  "Supplier Type": "விநியோகஸ்தர்களின் வகைகள்",
  // "PAN": "பான்",
  // "Aadhaar": "ஆதார்",
  "Registration No.": "பதிவு எண்",
  // "FSSAI Licence": "எஃப்எஸ்எஸ்ஏஐ உரிமம்",
  "Preferred Payment Terms": "விருப்பமான கட்டண விதிமுறைகள்",
  "Village / Taluk": "கிராமம் / தாலுகா",
  "State": "மாநிலம்",
  "Pincode": "அஞ்சல் குறியீடு",
  "Account Holder": "கணக்கு வைத்திருப்பவர்",
  "Bank / Branch": "வங்கி / கிளை",
  "Account No.": "கணக்கு எண்",
  // "IFSC": "ஐஎஃப்எஸ்சி",
  // "UPI ID": "யுபிஐ ஐடி",
  // Client 2026-08-05: reads as "Seasonal Product" rather than a bare
  // adjective. பருவகால -> பருவகால பொருள் (adds the noun).
  "Seasonal Product": "பருவகால பொருள்",
  // Client 2026-08-05: paired with "Seasonal Product" — singular, since both
  // describe one product. Was "கெட்டுபோகும் பொருட்கள்" (plural).
  "Perishable Product": "கெட்டுபோகும் பொருள்",
  "Stock in Hand": "கையிருப்பு அளவு",
  "Stock Ready to Supply": "விநியோகிக்க தயார்நிலையிலுள்ள பொருட்கள்",
  "Supply Regions": "விநியோகிக்கப்படும் மண்டலம்",
  "Profile": "சுயவிவரம்",
  "Published Products": "வெளியிடப்பட்ட பொருட்கள்",
  "Entity Type": "நிறுவன வகை",
  "Stock": "கையிருப்பு",
  "Stock Availability": "கையிருப்பு",
  "Pricing": "விலை",
  "Photos": "புகைப்படங்கள்",
  "Approve Supplier": "விநியோகஸ்தரை அங்கிகரித்தல்",
  "Reject Participant": "பங்கேற்பாளரை நிராகரித்தல்",
  "Back to queue": "வரிசைக்குத் திரும்பு",
  "Supplier Review Queue": "சப்ளையர் மறுஆய்வு வரிசை",
  "Review, approve, or reject supplier registrations.": "சப்ளையர் பதிவுகளை மறுஆய்வு செய்யவும், அங்கீகரிக்கவும் அல்லது நிராகரிக்கவும்.",
  "No suppliers match this filter.": "இந்த வடிகட்டலுக்கு பொருந்தும் சப்ளையர்கள் இல்லை.",
  "No products listed yet.": "இதுவரை பொருட்கள் பட்டியலிடப்படவில்லை.",
  "No address on file.": "பதிவில் முகவரி இல்லை.",
  "No bank details on file.": "பதிவில் வங்கி விவரங்கள் இல்லை.",
  "No documents uploaded yet.": "இதுவரை ஆவணங்கள் பதிவேற்றப்படவில்லை.",
  "No delivery districts configured yet.": "இதுவரை விநியோக மாவட்டங்கள் அமைக்கப்படவில்லை.",
  "No photos uploaded.": "புகைப்படங்கள் பதிவேற்றப்படவில்லை.",
  "No pricing configured yet.": "இதுவரை விலை நிர்ணயிக்கப்படவில்லை.",
  "Approve this supplier's registration?": "இந்த சப்ளையரின் பதிவை அங்கீகரிக்கவா?",

  // ---- Price Review / Quality Check / Rate Fixing (added 2026-07-29) ----
  // "Verification Required": "சரிபார்ப்பு தேவை",
  "Send OTP to my email": "எனது மின்னஞ்சலுக்கு ஓடிபி அனுப்பவும்",
  "Verify & Continue": "சரிபார்த்து தொடரவும்",
  // "Enter OTP": "ஓடிபியை உள்ளிடவும்",
  "Resend OTP": "ஒருமுறை கடவுச்சொல் மீண்டும் அனுப்புதல்",
  "Participant Bids": "பங்கேற்பாளர் விலைமனுக்கள்",
  "Participant": "பங்கேற்பாளர்",
  "Confirm Allocation": "ஒதுக்கீட்டை உறுதிப்படுத்து",
  "Award Summary": "விருது சுருக்கம்",
  "Reason Log": "காரண பதிவு",
  "Assign Stores": "கடைகளை ஒதுக்கு",
  "Save Store Assignments": "கடை ஒதுக்கீடுகளை சேமிக்கவும்",
  "Required": "தேவை",
  "Use the mobile number linked to your PAN": "உங்கள் பான் அட்டையுடன் இணைக்கப்பட்ட மொபைல் எண்ணைப் பயன்படுத்தவும்",
  "Allocated so far": "இதுவரை ஒதுக்கப்பட்டது",
  "Remaining": "மீதமுள்ளது",
  "Reason": "காரணம்",
  "Certifications": "சான்றிதழ்கள்",
  "Allocate": "ஒதுக்கீடு செய்",
  "No closed procurement rounds right now — a round must be Closed before award decisions can be made.": "தற்போது மூடப்பட்ட கொள்முதல் சுற்றுகள் இல்லை — விருது முடிவுகள் எடுப்பதற்கு முன் சுற்று மூடப்பட்டிருக்க வேண்டும்.",
  "No participants have submitted a bid for this product yet — there's nothing to compare or award until one does.": "இந்த பொருளுக்கு இதுவரை எந்த பங்கேற்பாளரும் விலைமனு சமர்ப்பிக்கவில்லை — ஒருவர் சமர்ப்பிக்கும் வரை ஒப்பிட அல்லது வழங்க எதுவும் இல்லை.",
  "No tenders exist yet.": "இதுவரை டெண்டர்கள் இல்லை.",
  "No products on this tender.": "இந்த டெண்டரில் பொருட்கள் இல்லை.",
  "No suppliers have submitted a bid for this product yet.": "இந்த பொருளுக்கு இதுவரை எந்த சப்ளையரும் விலைமனு சமர்ப்பிக்கவில்லை.",
  "No active products found.": "செயலில் உள்ள பொருட்கள் எதுவும் இல்லை.",
  "No price recorded yet": "இன்னும் விலை பதிவு செய்யப்படவில்லை",
  "History": "வரலாறு",
  // Client 2026-08-05: the Rate Fixation page calls its price history
  // "Price Fixing Details" rather than a bare "History".
  "Price Fixing Details": "விலை நிர்ணய விவரங்கள்",
  "Hide History": "வரலாற்றை மறை",
  "Record Price": "விலையை பதிவு செய்",
  // Client 2026-08-05: "Record New Price" — the action always adds a new
  // dated entry rather than editing the existing one, so the wording says so.
  "Record New Price": "புதிய விலையை பதிவு செய்",
  "Sample Photo": "மாதிரி புகைப்படம்",
  "Report": "அறிக்கை",
  "Submit Report": "அறிக்கையை சமர்ப்பிக்கவும்",
  "Bidding Suppliers": "விலைமனு சப்ளையர்கள்",

  // ---- MyIndents / CreateIndent / IndentDetail / IndentReview(Detail) / IndentDemand / TenderList / CreateTender / TenderDetail (added 2026-07-29) ----
  "Submitted": "சமர்ப்பிக்கப்பட்டது",
  "Total indents": "மொத்த தேவைப்பட்டியல்",
  "Indent Submitted": "தேவைப்பட்டியல் சமர்ப்பிக்கப்பட்டது",
  "Supply Order Completed": "பொருட்கள் வழங்கப்பட்டது",
  "Cancelled": "ரத்து செய்யப்பட்டது",
  "You haven't submitted any indents yet.": "நீங்கள் இதுவரை எந்த தேவைப்பட்டியலும் சமர்ப்பிக்கவில்லை.",
  "Back to My Indents": "எனது தேவைப்பட்டியல்களுக்குத் திரும்பு",
  "Indent Details": "தேவைப்பட்டியலின் விவரங்கள்",
  // "View it": "இதைப் பார்",
  // "Remarks (optional)": "குறிப்புகள் (விருப்பத்தேர்வு)",
  "Submit Indent": "இண்டெண்ட் சமர்ப்பிக்கவும்",
  "Grade / Spec": "தரம் / விவரக்குறிப்பு",
  "Stage": "கொள்முதல் நிலை",
  "Items": "பொருட்கள்",
  // "Cancel Indent": "தேவைப்பட்டியலை ரத்துசெய்",
  "Cancel this indent? This cannot be undone.": "இந்த இண்டெண்டை ரத்து செய்யவா? இதை மீட்டெடுக்க முடியாது.",
  // "Photo of Goods Received": "பெறப்பட்ட பொருட்களின் புகைப்படம்",
  // "All statuses": "அனைத்து நிலைகளும்",
  "Submitted On": "சமர்ப்பிக்கப்பட்ட தேதி",
  "No indents match this filter.": "இந்த வடிகட்டலுடன் பொருந்தும் தேவைப்பட்டியல் இல்லை.",
  "Back to Indent Review": "இண்டெண்ட் மறுஆய்விற்குத் திரும்பு",
  "Submitted By": "சமர்ப்பித்தவர்",
  "Indent Demand by Product": "பொருள் வாரியான இண்டெண்ட் தேவை",
  "Total Demand": "மொத்த தேவை",
  "Indented Products": "தேவைப்படும் பொருட்கள்",
  "No. of Indents Received":"பெறப்பட்ட மொத்த தேவைப்பட்டியலின் எண்ணிக்கை",
  "No pending demand.": "நிலுவையில் உள்ள தேவை இல்லை.",
  "No pending demand": "நிலுவையில் உள்ள தேவை இல்லை",
  "No demand for this product.": "இந்த பொருளுக்கு தேவை இல்லை.",
  "Pool New Procurement": "புதிய கொள்முதல் திரட்டு",
  "Total procurement": "மொத்த கொள்முதல்",
  "Open Procurement": "திறந்த கொள்முதல்",
  "Open Procurement — Not Yet Bid": "திறந்த கொள்முதல் — இன்னும் ஏலமிடவில்லை",
  // "Draft": "வரைவு",
  "Open": "திறந்தது",
  "Closed": "மூடப்பட்டது",
  "Procurement No.": "கொள்முதல் எண்",
  "No procurement yet — pool submitted indents into one to get started.": "கொள்முதல் இதுவரை இல்லை — தொடங்க சமர்ப்பிக்கப்பட்ட தேவைப்பட்டியல் திரட்டவும்.",
  "Back to Procurement": "கொள்முதலுக்குத் திரும்பு",
  // "Procurement Month": "கொள்முதல் மாதம்",
  "No active products exist yet.": "இதுவரை செயலில் உள்ள பொருட்கள் இல்லை.",
  "Indents": "தேவைப்பட்டியல்கள்",
  "Procurement Details": "கொள்முதல் விவரங்கள்",
  // "Response Deadline (optional)": "பதில் காலக்கெடு (விருப்பத்தேர்வு)",
  // "Delivery Period (optional)": "விநியோக காலம் (விருப்பத்தேர்வு)",
  // "Continue to Preview": "முன்னோட்டத்திற்குச் செல்",
  // "Confirm & Create Procurement": "உறுதிப்படுத்தி கொள்முதலை உருவாக்கு",
  "View this procurement": "இந்த கொள்முதலைப் பார்",
  "Skipped": "தவிர்க்கப்பட்டது",
  "Done": "முடிந்தது",
  "Pooled Indents": "திரட்டப்பட்ட தேவைப்பட்டியல்கள்",
  "No stores have requested this product this month.": "இந்த மாதம் எந்த கடையும் இந்த பொருளை கோரவில்லை.",
  "Open Price Comparison & Award": "விலை ஒப்பீடு & வழங்கல் திற",
  "Allocated Qty": "ஒதுக்கப்பட்ட அளவு",
  "Delivers to": "விநியோகிக்கப்படும் இடம்",
  "Store assignment still pending for this allocation.": "இந்த ஒதுக்கீட்டிற்கான கடை நியமனம் இன்னும் நிலுவையில் உள்ளது.",
  "Rejected Candidates": "நிராகரிக்கப்பட்ட விண்ணப்பதாரர்கள்",
  // "Response Deadline": "பதில் காலக்கெடு",
  // "Delivery Period": "விநியோக காலம்",
  "Supplier Submissions": "சப்ளையர் சமர்ப்பிப்புகள்",
  "(rates only visible in Price Review)": "(விலைகள் விலை மறுஆய்வில் மட்டுமே தெரியும்)",
  "Approve & Open to Participants": "ஒப்புதல் அளித்து பங்கேற்பாளர்களுக்கு தெரிவித்தல்",
  "Close Procurement": "கொள்முதலை மூடு",
  "Cancel Procurement": "கொள்முதலை ரத்துசெய்",
  "Qty Needed": "தேவையான அளவு",
  "Product Demand": "பொருள் தேவை",
  "Cancel this procurement? Pooled indents will become available to pool into a future procurement again.": "இந்த கொள்முதலை ரத்து செய்யவா? திரட்டப்பட்ட தேவைப்பட்டியல் எதிர்கால கொள்முதலுக்கு மீண்டும் கிடைக்கும்.",
  "No pooled indents.": "திரட்டப்பட்ட தேவைப்பட்டியல்கள் இல்லை.",

  // ---- Dashboard (Pages/Admin/Dashboard.jsx, added 2026-08-04 — was
  // missing entirely, so BiLabel silently fell back to English-only) ----
  "Welcome back": "மீண்டும் வரவேற்கிறோம்",
  "Indent Pipeline": "தேவைப்பட்டியல் செயல்முறை",
  "My Indent Pipeline": "எனது தேவைப்பட்டியல் செயல்முறை",
  // "No indent items yet.": "இதுவரை தேவைப்பட்டியல் உருப்படிகள் இல்லை.",
  "No indent submitted yet for": "இதுவரை தேவைப்பட்டியல் சமர்ப்பிக்கப்படவில்லை",
  "Pending Receipts": "நிலுவையில் உள்ள பெறுதல்கள்",
  "Recent Awards": "சமீபத்திய வழங்கல்கள்",
  "No awards yet.": "இதுவரை வழங்கல்கள் இல்லை.",
  "Pending Price Reviews": "நிலுவையில் உள்ள விலை மறுஆய்வுகள்",
  "Awaiting Award Decision": "வழங்கல் முடிவுக்காக காத்திருக்கிறது",
  "Supply Pending": "விநியோகம் நிலுவையில்",
  "Pending Supplier Reviews": "நிலுவையில் உள்ள சப்ளையர் மறுஆய்வுகள்",
  "Received": "பெறப்பட்டது",
  "awarded supply-order lines": "வழங்கப்பட்ட கொள்முதல் ஆணை வரிகள்",
  "Unit" : "அலகு",
  // ---- Product Master form (added by frontend-tamil-3) ----
  // "HSN Code": "எச்எஸ்என் குறியீடு",
  "Shelf Life (Days)": "சேமிப்பு ஆயுள் (நாட்கள்)",
  "Select unit of measure": "அளவீட்டு அலகைத் தேர்ந்தெடுக்கவும்",

  // ---- Supplier-facing pages: dashboard/products/tenders/certificates/
  // deliveries/resubmit (added for the supplier-side Tailwind pages;
  // labels also used elsewhere above are reused, not duplicated) ----
  // Client 2026-08-05: "கேஒய்சி" was just a transliteration of "KYC";
  // "தேவையான ஆவணங்கள்" (required documents) reads naturally in Tamil.
  "KYC Documents": "தேவையான ஆவணங்கள்",
  // Client 2026-08-05: was "Products Listed" / "பட்டியலிடப்பட்ட பொருட்கள்".
  "Available Products": "கிடைக்கும் பொருட்கள்",
  // Client 2026-08-05: the supplier-facing label for the DB status
  // "Published" is "Ready to Supply". The stored value is unchanged — only
  // what the supplier reads. The count cards read "Products ready to supply".
  "Ready to Supply": "விநியோகிக்க தயார்",
  "Not Ready to Supply": "விநியோகிக்க தயாராக இல்லை",
  "Products ready to supply": "விநியோகிக்க தயார் நிலையில் உள்ள பொருட்கள்",
  // Client 2026-08-05: the supplier's bid count reads as "Price Quoted to
  // TNCCF" — plainer than "Bids Submitted" from the supplier's side.
  "Price Quoted to TNCCF": "TNCCF-க்கு தெரிவிக்கப்பட்ட விலை",
  "Price Accepted by TNCCF": "TNCCF ஏற்றுக்கொண்ட விலை",
  "Under Negotiation": "பேச்சுவார்த்தையில் உள்ளது",
  // Client 2026-08-05: avoid the word "bid" in supplier-facing copy — from
  // the supplier's side this is quoting a price, not bidding.
  "Price Not Quoted by us": "நாம் விலை தெரிவிக்காதவை",
  "Published": "வெளியிடப்பட்டது",
  "Verified": "சரிபார்க்கப்பட்டது",
  "Unverified": "சரிபார்க்கப்படவில்லை",
  "Products I Can Supply": "வழங்க இயலும் பொருட்கள்",
  "Upload": "பதிவேற்று",
  "Replace": "மாற்று",
  "No documents required for this entity type.": "இந்த நிறுவன வகைக்கு ஆவணங்கள் எதுவும் தேவையில்லை.",

  "Add a Product": "ஒரு பொருளைச் சேர்",
  "You haven't added any products yet.": "நீங்கள் இதுவரை எந்த பொருளையும் சேர்க்கவில்லை.",
  "Pricing & Stock": "விலை மற்றும் இருப்பு",
  "Stock & Availability": "இருப்பு மற்றும் கிடைக்கும் தன்மை",
  "Publish": "வெளியிடு",
  "Unpublish": "வெளியிடாதே",
  // Client 2026-08-05: the button now reads in the same terms as the status
  // it sets ("Ready to Supply"), rather than Publish/Unpublish.
  "Mark Ready to Supply": "விநியோகிக்க தயார் என குறி",
  "Mark Not Ready": "தயாராக இல்லை என குறி",
  "Remove Photo": "புகைப்படத்தை அகற்று",
  "Add District": "மாவட்டம் சேர்",
  "Edit District": "மாவட்டத்தைத் திருத்து",
  "Save District": "மாவட்டத்தைச் சேமி",
  "Add Blackout Date": "தடை நாள் சேர்",
  "Blackout Dates": "தடை நாட்கள்",
  "Cold Chain": "குளிர் சங்கிலி",

  "Bid Submitted": "ஏலம் சமர்ப்பிக்கப்பட்டது",
  "Price Quoted": "விலை தெரிவிக்கப்பட்டது",
  "Quantity Required & Supply Regions": "தேவையான அளவு & வழங்கல் பகுதிகள்",
  "Your Bid": "உங்கள் ஏலம்",
  "Your Quoted Price": "நீங்கள் தெரிவித்த விலை",
  "View Declaration": "பிரகடனத்தைப் பார்",
  "View Lab Certificate": "ஆய்வக சான்றிதழைப் பார்",
  "Update Bid": "ஏலத்தைப் புதுப்பி",
  "Update Price": "விலையைப் புதுப்பி",
  "Withdraw Bid": "ஏலத்தை திரும்பப்பெறு",
  "Withdraw Price": "விலையை திரும்பப்பெறு",
  "Award & Delivery Details": "வழங்கல் & விநியோக விவரங்கள்",
  "Agreed Rate": "ஒப்புக்கொள்ளப்பட்ட விலை",
  "Agreed Quantity": "ஒப்புக்கொள்ளப்பட்ட அளவு",
  "Variety": "வகை",
  "Deliver To": "விநியோகிக்க வேண்டிய இடம்",
  "Special Rate": "சிறப்பு விலை",
  "Standard Rate": "தரநிலை விலை",
  "Lab Certificate No.": "ஆய்வக சான்றிதழ் எண்",
  "Certifying Authority": "சான்றளிக்கும் அதிகாரம்",
  "Lab Cert Issue Date": "ஆய்வக சான்றிதழ் வழங்கிய தேதி",
  "Lab Cert Expiry Date": "ஆய்வக சான்றிதழ் காலாவதி தேதி",
  "Lab-Test Certificate": "ஆய்வக சோதனை சான்றிதழ்",
  "Commodity Photographs": "பொருள் புகைப்படங்கள்",
  "Signed Declaration / Acknowledgement": "கையொப்பமிடப்பட்ட பிரகடனம் / ஒப்புகை",

  "Statutory Details": "சட்டப்பூர்வ விவரங்கள்",
  "Name / Entity Name": "பெயர் / நிறுவனப் பெயர்",
  // "FSSAI Licence Number": "எஃப்எஸ்எஸ்ஏஐ உரிம எண்",
  // "FSSAI Expiry Date": "எஃப்எஸ்எஸ்ஏஐ காலாவதி தேதி",
  "Address Line 1": "முகவரி வரி 1",
  "Address Line 2": "முகவரி வரி 2",
  "Village / Town": "கிராமம் / நகரம்",
  "Taluk": "தாலுகா",
  "Account Holder Name": "கணக்கு வைத்திருப்பவரின் பெயர்",
  "Bank Name": "வங்கியின் பெயர்",
  "Branch Name": "கிளையின் பெயர்",
  "Confirm Account No.": "கணக்கு எண்ணை உறுதிப்படுத்தவும்",
  "Add another document": "மற்றொரு ஆவணத்தைச் சேர்",
  "Type": "வகை",
  "Certification Name": "சான்றிதழ் பெயர்",
  "Certificate / Licence Number": "சான்றிதழ் / உரிம எண்",
  "Choose file & add": "கோப்பைத் தேர்ந்தெடுத்து சேர்",
  "Next": "அடுத்து",
  "Submit Registration": "பதிவை சமர்ப்பிக்கவும்",
  "Registration Complete": "பதிவு முடிந்தது",
  "Go to Login": "உள்நுழைவுக்குச் செல்",
  "GST Certificate": "ஜிஎஸ்டி சான்றிதழ்",
  "Registration Certificate": "பதிவுச் சான்றிதழ்",
  "Bank Passbook / Cancelled Cheque": "வங்கி பாஸ்புக் / ரத்து செய்யப்பட்ட காசோலை",
  "Authorised Person's Aadhaar": "அங்கீகரிக்கப்பட்ட நபரின் ஆதார்",
  // "FSSAI": "எஃப்எஸ்எஸ்ஏஐ",
  "AGMARK": "அக்மார்க்",
  "BIS": "பிஐஎஸ்",
  "ISO 22000": "ஐஎஸ்ஓ 22000",
  "NPOP Organic": "என்பிஓபி ஆர்கானிக்",
  "Jaivik Bharat": "ஜைவிக் பாரத்",
  "State Horticulture Department Certification": "மாநில தோட்டக்கலைத் துறை சான்றிதழ்",
  "GI Tag Registration": "புவிசார் குறியீடு பதிவு",
  "NABL Accreditation": "நேபிள் அங்கீகாரம்",
  "MSME (Millets)": "எம்எஸ்எம்இ (சிறுதானியங்கள்)",
  "Other": "மற்றவை",

  "My Certificates": "எனது சான்றிதழ்கள்",
  "Add Certificate": "சான்றிதழ் சேர்",
  "Certificate Type": "சான்றிதழ் வகை",
  "Certificate Number": "சான்றிதழ் எண்",
  "Certificate No.": "சான்றிதழ் எண்",
  "File": "கோப்பு",
  "Issue Date": "வழங்கிய தேதி",
  "Expiry Date": "காலாவதி தேதி",
  "Save Certificate": "சான்றிதழைச் சேமிக்கவும்",
  "Quality Certifications": "தரச் சான்றிதழ்கள்",
  "Issued": "வழங்கப்பட்டது",
  "Expires": "காலாவதியாகும்",
  "Expired": "காலாவதியானது",
  "Expiring soon": "விரைவில் காலாவதியாகும்",

  "Awarded": "வழங்கப்பட்டது",

  "Verify Mobile": "மொபைலைச் சரிபார்",
  "Review & Resubmit": "மறுஆய்வு & மீண்டும் சமர்ப்பி",
  "One-Time Password": "ஒரு முறை கடவுச்சொல்",
  // "Send OTP": "ஓடிபி அனுப்பு",
  "Verify": "சரிபார்",
  "Change mobile number": "மொபைல் எண்ணை மாற்று",
  "Aadhaar Number": "ஆதார் எண்",
  // "SHG / FPO / JLG Registration No.": "எம்ஓசி / எஸ்எச்ஜி / ஜேஎல்ஜி பதிவு எண்",
  "Cooperative Registration No.": "கூட்டுறவு பதிவு எண்",
  "CIN / MSME Udyam No.": "சிஐஎன் / எம்எஸ்எம்இ உத்யம் எண்",
  "MSME Udyam Registration No.": "எம்எஸ்எம்இ உத்யம் பதிவு எண்",
  "Issued by": "வழங்கியவர்",
  "Add another": "மற்றொன்று சேர்",
  "Reason for rejection": "நிராகரிக்கப்பட்டதற்கான காரணம்",
  "Resubmit for Review": "மறுஆய்வுக்கு மீண்டும் சமர்ப்பிக்கவும்",
  "Resubmitted for Review": "மறுஆய்வுக்கு மீண்டும் சமர்ப்பிக்கப்பட்டது",
  "Indent Created" : "தேவைப்பட்டியல் உருவாக்கப்பட்டது",
  "Pending Indents" : "தேவைப்பட்டியல் நிலுவையில் உள்ளது",
  "Indents Supplied" : "முடிவடைந்த தேவைப்பட்டியல்",
  "Indents Completed" : "தேவைபட்டியல்  வழங்கப்பட்டது",
  "Recent Supply Details": "சமீபத்திய விநியோக விவரங்கள்",
  "Indent Cancelled": "தேவைப்பட்டியல் ரத்து செய்யப்பட்டது",
  // "Indent Received": "தேவைப்பட்டியல் பெறப்பட்டது",
  "MONTH": "மாதம்",
  "Date": "தேதி",
  "Current Status": "தற்போதைய நிலை",
  // "From Date": "தொடக்க தேதி",
  // "To Date": "முடிவு தேதி",
  // "From Month": "தொடக்க மாதம்",
  // "To Month": "முடிவு மாதம்",
  "S.No": "வ.எண்",
  "Name of the Product": "பொருட்களின் பெயர்",
  "Required Quantity": "தேவைப்படும் அளவு",
  // ---- CMS purchase-source declaration (2026-08-07) ----
  // Taken verbatim from the paper form this screen replaces
  // ("கூட்டுறவு கொள்முதல் குழுவிற்கான விவசாயிகளிடமிருந்து கொள்முதல் விவரம்"),
  // so a clerk keying from the sheet reads the same words in the same order.
  "Purchase Source Declaration": "கொள்முதல் விவரம்",
  // The form's first column (client 2026-08-07). Replaced the society-number
  // column 069 originally modelled — see migration 070.
  "Society District": "கூட்டுறவு விற்பனை சங்கத்தின் மாவட்டம்",
  // Full form (client 2026-08-07), matching the district column above rather
  // than the bare "சங்கத்தின் பெயர்" this started as.
  "Society Name": "கூட்டுறவு விற்பனை சங்கத்தின் பெயர்",
  // Client-specified wording (2026-08-07), replacing the earlier
  // "கொள்முதல் செய்த நபரின் பெயர்". Note the underlying column is
  // mst_jpc_cms_source_row.person_name — the DB field and API contract are
  // unchanged; this is the label only.
  "Purchased From": "கூட்டுக் கொள்முதல் குழு மூலம் கொள்முதல் ஆணை பெறப்பட்ட நுகர்பொருளின் பெயர்",
  // Client-specified wording (2026-08-07), replacing "உறுப்பினர் எண்". The
  // underlying column is still mst_jpc_cms_source_row.member_no — label only,
  // no schema or API change. See the note in SupplierSourceDeclarations.jsx.
  "Member No.": "கொள்முதல் ஆணை பெறப்பட்ட மாவட்டத்தின் பெயர்",
  // Its own key rather than reusing "Mobile" (which resolves to தொலைப்பேசி
  // for SupplierDashboard.jsx's contact block) — the paper form says
  // கைபேசி எண், and changing the shared key would alter that page too.
  // Client-specified wording (2026-08-07), replacing "கைபேசி எண்". Underlying
  // column is still mst_jpc_cms_source_row.mobile_no — label only; the field
  // rename pass comes after every heading is settled.
  "Mobile No.": "கொள்முதல் ஆணை பெறப்பட்ட அளவு (கிலோவில்)",
  // Client-specified wording (2026-08-07), replacing "சர்வே எண்". Underlying
  // column is still mst_jpc_cms_source_row.survey_no — label only.
  "Survey No.": "பெயர்",
  // Client-specified wording (2026-08-07), replacing "கிராமத்தின் பெயர்".
  // Underlying column is still mst_jpc_cms_source_row.village_name — label
  // only. (The separate "Village / Taluk" key above belongs to another screen.)
  "Village": "சங்க உறுப்பினர் எண்",
  // Client-specified wording (2026-08-07), replacing "பட்டா எண்". Underlying
  // column is still mst_jpc_cms_source_row.patta_no — label only.
  // Spelled கைப்பேசி as the client wrote it here; "Mobile Number" elsewhere in
  // this dictionary uses கைபேசி and is left alone (different screen).
  "Patta No.": "கைப்பேசி எண்",
  // Client-specified wording (2026-08-07), replacing "அளவு (கிலோ)". This is the
  // paper form's merged land-reference cell. NOTE: the underlying column is
  // still mst_jpc_cms_source_row.quantity_kg (DECIMAL) and is still validated
  // as a positive number / summed into the sheet total — resolved in the
  // field-rename pass, per the client's "headings first" sequencing.
  "Qty (kg)": "சர்வே எண் / கிராமத்தின் பெயர் / பட்டா எண்",
  // "Products" already exists above — not redefined here, since a duplicate
  // key would silently shadow the earlier one.
  "Total": "மொத்தம்",

  // ---- DocumentRequirementsModal.jsx (added 2026-09-03) — the "what
  // documents will I need?" reference popup shown from the Login page's
  // Supplier option info icon. Mirrors entityRequirements.js/
  // SupplierRegister.jsx's own document set; a few keys reuse strings
  // already defined earlier in this dictionary (GST Certificate,
  // Registration Certificate, ISO 22000, etc.) — not redefined here.
  "Documents you'll need to register": "பதிவு செய்ய தேவையான ஆவணங்கள்",
  "Every business type below needs an authorised signatory's ID, a bank passbook, GST certificate, and an FSSAI licence for the firm.":
    "கீழே உள்ள ஒவ்வொரு வணிக வகைக்கும் அங்கீகரிக்கப்பட்ட கையொப்பமிடுபவரின் ஆதார், வங்கி பாஸ்புக், ஜிஎஸ்டி சான்றிதழ் மற்றும் நிறுவனத்திற்கான எஃப்எஸ்எஸ்ஏஐ உரிமம் தேவை.",
  "Required Documents": "தேவையான ஆவணங்கள்",
  "Covers:": "பொருந்தும் வகைகள்:",

  // Entity type labels
  "Private Firm / Company": "தனியார் நிறுவனம் / கம்பெனி",
  "SHG / FPO / JLG": "சுயஉதவிக் குழு / விவசாய உற்பத்தியாளர் நிறுவனம் / கூட்டுப் பொறுப்புக் குழு",
  "Cooperative Society": "கூட்டுறவு சங்கம்",
  "Miller / MSME": "ஆலை உரிமையாளர் / எம்எஸ்எம்இ",

  // Supplier type names (shown in each entity card's "Covers:" line)
  "Authorised Dealer": "அங்கீகரிக்கப்பட்ட விற்பனையாளர்",
  "Manufacturer": "உற்பத்தியாளர்",
  "Producer": "உற்பத்தி செய்பவர்",
  "CMS": "கூட்டுறவு விற்பனைச் சங்கம்",
  "LAMPS": "பெரிய பரப்பு பல்நோக்குக் கூட்டுறவு சங்கம்",
  "Others": "மற்றவை",

  // Per-entity registration-document description
  "Company registration proof (CIN) or MSME Udyam Registration Certificate": "நிறுவனப் பதிவுச் சான்று (சிஐஎன்) அல்லது எம்எஸ்எம்இ உத்யம் பதிவுச் சான்றிதழ்",
  "SHG / FPO / JLG Registration Certificate": "சுயஉதவிக் குழு / விவசாய உற்பத்தியாளர் நிறுவனம் / கூட்டுப் பொறுப்புக் குழு பதிவுச் சான்றிதழ்",
  "Cooperative Society Registration Certificate": "கூட்டுறவு சங்க பதிவுச் சான்றிதழ்",
  "MSME Udyam Registration Certificate": "எம்எஸ்எம்இ உத்யம் பதிவுச் சான்றிதழ்",

  // Common required-document bullets (this modal's own phrasing, distinct
  // casing from the registration wizard's own "Authorised Person's Aadhaar")
  "Authorised person's Aadhaar": "அங்கீகரிக்கப்பட்ட நபரின் ஆதார்",
  "Bank Passbook first page or a cancelled cheque": "வங்கிச் சேமிப்புப் புத்தகத்தின் முதல் பக்கம் அல்லது ரத்து செய்யப்பட்ட காசோலை",
  "GST Registration Certificate": "ஜிஎஸ்டி பதிவுச் சான்றிதழ்",
  "FSSAI Food Business Licence for your firm": "உங்கள் நிறுவனத்திற்கான எஃப்எஸ்எஸ்ஏஐ உணவு வணிக உரிமம்",

  // Entity-specific mandatory/optional/conditional certificates
  "Chitta / Adangal (land record)": "சிட்டா / அடங்கல் (நில பதிவு)",
  "Current Crop Loan proof (bank name + account number)": "தற்போதைய பயிர் கடன் சான்று (வங்கியின் பெயர் + கணக்கு எண்)",
  "Society's Board/General-Body Resolution Copy": "சங்கத்தின் நிர்வாகக் குழு / பொதுக் குழு தீர்மான நகல்",
  "Factory Licence / Fire Safety Licence": "தொழிற்சாலை உரிமம் / தீ பாதுகாப்பு உரிமம்",
  "ISO 22000 Certification": "ஐஎஸ்ஓ 22000 சான்றிதழ்",
  "Auditor Financial Statements (last 3 years)": "தணிக்கையாளர் நிதி அறிக்கைகள் (கடந்த 3 ஆண்டுகள்)",
  "Farmer Registration Certificate": "விவசாயி பதிவுச் சான்றிதழ்",

  // Conditional-callout sentence fragments
  "If registering as Farmer:": "'விவசாயி' ஆக பதிவு செய்தால்:",
  "also required —": "இதுவும் தேவை —",
  "If registering as": "இவ்வாறு பதிவு செய்தால்",
  "also required:": "இதுவும் தேவை:",
  "or": "அல்லது",
};

export function bi(label) {
  if (!label) return label;
  const tamil = TAMIL_LABELS[label];
  return tamil ? `${label} (${tamil})` : label;
}

// Same English+Tamil pairing as bi(), but on two lines instead of one —
// for AppTable headerName, which turns "\n" into a <br/> (see
// Components/table/AppTable.jsx's renderHeaderText). Keeps narrow table
// header cells from forcing horizontal scroll with a long inline string.
export function biStack(label) {
  if (!label) return label;
  const tamil = TAMIL_LABELS[label];
  return tamil ? `${label}\n(${tamil})` : label;
}

// Stacked variant of bi() — English on top, "(Tamil)" on its own line
// below in smaller muted text. Use for table headers, field/stat labels,
// and static copy; NOT for buttons (use bi() there) and NOT for AppTable
// `headerName` config (that's a plain string prop — use biStack() there,
// AppTable already renders its "\n" as a line break).
export function BiLabel({ label, component = "span" }) {
  if (!label) return label;
  const tamil = TAMIL_LABELS[label];
  if (!tamil) return label;
  return (
    <Box component={component} sx={{ display: "inline-block", lineHeight: 1.25 }}>
      {label}
      <Box component="span" sx={{ display: "block", fontSize: "0.78em", color: "text.secondary", fontWeight: 400 }}>
        ({tamil})
      </Box>
    </Box>
  );
}

export default TAMIL_LABELS;
